
import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast"; // Fixed import path
import { supabase } from "@/integrations/supabase/client";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { 
  AlertCircle, 
  Cpu, 
  Wifi, 
  WifiOff, 
  ActivitySquare, 
  RefreshCw,
  Database,
  Info,
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocation, useNavigate } from "react-router-dom";

const DeviceDiagnostic = () => {
  const { toast } = useToast();
  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingPing, setSendingPing] = useState(false);
  const [sendingDiagnostic, setSendingDiagnostic] = useState(false);
  const [networkStatus, setNetworkStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [supabaseStatus, setSupabaseStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const navigate = useNavigate();
  
  // Check if Supabase is working
  useEffect(() => {
    const checkSupabase = async () => {
      try {
        const { data, error } = await supabase.from('devices').select('count');
        if (error) throw error;
        setSupabaseStatus('connected');
      } catch (error) {
        console.error('Supabase connection error:', error);
        setSupabaseStatus('error');
      }
    };
    
    checkSupabase();
  }, []);
  
  // Check if network is online
  useEffect(() => {
    const checkNetwork = () => {
      setNetworkStatus(navigator.onLine ? 'online' : 'offline');
    };
    
    checkNetwork();
    window.addEventListener('online', checkNetwork);
    window.addEventListener('offline', checkNetwork);
    
    return () => {
      window.removeEventListener('online', checkNetwork);
      window.removeEventListener('offline', checkNetwork);
    };
  }, []);
  
  // Fetch all devices (devices table is no longer in the realtime publication,
  // so we poll instead; paused while the tab is hidden)
  useEffect(() => {
    fetchDevices();

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchDevices();
      }
    }, 30000);

    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchDevices();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  
  const fetchDevices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .order('last_seen', { ascending: false });
          
      if (error) throw error;
      
      console.log('Fetched devices:', data);
      setDevices(data || []);
    } catch (error: any) {
      console.error('Error fetching devices:', error);
      toast({
        variant: "destructive",
        title: "Error loading devices",
        description: error.message || "Could not load device information"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Force refresh data
  const refreshData = async () => {
    setRefreshing(true);
    await fetchDevices();
    toast({
      title: "Data refreshed",
      description: "Device information has been updated",
    });
    setRefreshing(false);
  };
  
  // Check if device is online (seen in last 5 minutes)
  const isDeviceOnline = (lastSeen: string) => {
    if (!lastSeen) return false;
    const fiveMinutesAgo = new Date();
    fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
    return new Date(lastSeen) > fiveMinutesAgo;
  };
  
  // Send ping command to device
  const pingDevice = async (deviceId: string) => {
    setSendingPing(true);
    try {
      const { error } = await supabase
        .from('device_commands')
        .insert([
          { 
            device_id: deviceId, 
            command_type: 'ping', 
            params: { timestamp: new Date().toISOString() },
            processed: false
          }
        ]);
          
      if (error) throw error;
        
      toast({
        title: "Ping Sent",
        description: "Command sent to device. Check your serial monitor.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to ping device",
        description: error.message || "Error sending command to device",
      });
    } finally {
      setSendingPing(false);
    }
  };
  
  // Send diagnostic command to device
  const sendDiagnosticCommand = async (deviceId: string) => {
    setSendingDiagnostic(true);
    try {
      const { error } = await supabase
        .from('device_commands')
        .insert([{
          device_id: deviceId,
          command_type: 'diagnostic',
          params: { 
            timestamp: new Date().toISOString(),
            requestType: 'full-status' 
          },
          processed: false
        }]);

      if (error) throw error;

      toast({
        title: "Diagnostic requested",
        description: "Check your serial monitor for diagnostic information",
      });
    } catch (error: any) {
      console.error('Error sending diagnostic command:', error);
      
      toast({
        title: "Error",
        description: "Failed to send diagnostic command.",
        variant: "destructive",
      });
    } finally {
      setSendingDiagnostic(false);
    }
  };
  
  // Register a test device for debugging
  const registerTestDevice = async () => {
    try {
      setLoading(true);
      
      // Create a test device with a random MAC address
      const testMac = Array(6).fill(0).map(() => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join(':');
      const testDeviceId = testMac.replace(/:/g, '');
      
      const { error } = await supabase
        .from('devices')
        .insert([{
          device_id: testDeviceId,
          name: `Test_Device_${testDeviceId.substring(0, 4)}`,
          last_seen: new Date().toISOString()
        }]);
      
      if (error) throw error;
      
      toast({
        title: "Test device registered",
        description: "A test device has been added for diagnostic purposes",
      });
      
      fetchDevices();
    } catch (error: any) {
      console.error('Error registering test device:', error);
      toast({
        variant: "destructive",
        title: "Error registering test device",
        description: error.message || "Could not register test device"
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Device Diagnostics</h1>
          <div className="flex gap-2">
            <Button 
              onClick={refreshData} 
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            
            <Button 
              onClick={registerTestDevice}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Cpu className="h-4 w-4" />
              Register Test Device
            </Button>
          </div>
        </div>
        
        {/* System Status Card */}
        <Card className="mb-8">
          <CardHeader className="pb-2">
            <CardTitle>System Status</CardTitle>
            <CardDescription>Current status of your diagnostic system</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Network Status */}
              <div className="flex items-center p-4 border rounded-lg">
                <div className={`p-2 rounded-full mr-3 ${
                  networkStatus === 'online' ? 'bg-green-100' : 
                  networkStatus === 'offline' ? 'bg-red-100' : 'bg-yellow-100'
                }`}>
                  {networkStatus === 'online' ? (
                    <Wifi className="h-5 w-5 text-green-600" />
                  ) : networkStatus === 'offline' ? (
                    <WifiOff className="h-5 w-5 text-red-600" />
                  ) : (
                    <RefreshCw className="h-5 w-5 text-yellow-600 animate-spin" />
                  )}
                </div>
                <div>
                  <p className="font-medium">Network</p>
                  <p className={`text-sm ${
                    networkStatus === 'online' ? 'text-green-600' : 
                    networkStatus === 'offline' ? 'text-red-600' : 'text-yellow-600'
                  }`}>
                    {networkStatus === 'online' ? 'Connected' : 
                     networkStatus === 'offline' ? 'Disconnected' : 'Checking...'}
                  </p>
                </div>
              </div>
              
              {/* Supabase Status */}
              <div className="flex items-center p-4 border rounded-lg">
                <div className={`p-2 rounded-full mr-3 ${
                  supabaseStatus === 'connected' ? 'bg-green-100' : 
                  supabaseStatus === 'error' ? 'bg-red-100' : 'bg-yellow-100'
                }`}>
                  {supabaseStatus === 'connected' ? (
                    <Database className="h-5 w-5 text-green-600" />
                  ) : supabaseStatus === 'error' ? (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  ) : (
                    <RefreshCw className="h-5 w-5 text-yellow-600 animate-spin" />
                  )}
                </div>
                <div>
                  <p className="font-medium">Supabase</p>
                  <p className={`text-sm ${
                    supabaseStatus === 'connected' ? 'text-green-600' : 
                    supabaseStatus === 'error' ? 'text-red-600' : 'text-yellow-600'
                  }`}>
                    {supabaseStatus === 'connected' ? 'Connected' : 
                     supabaseStatus === 'error' ? 'Connection Error' : 'Checking...'}
                  </p>
                </div>
              </div>
              
              {/* Device Status */}
              <div className="flex items-center p-4 border rounded-lg">
                <div className={`p-2 rounded-full mr-3 ${
                  devices.length > 0 ? 'bg-green-100' : 'bg-yellow-100'
                }`}>
                  {devices.length > 0 ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  )}
                </div>
                <div>
                  <p className="font-medium">Devices</p>
                  <p className={`text-sm ${
                    devices.length > 0 ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {devices.length > 0 ? `${devices.length} device(s) found` : 'No devices detected'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Tabs defaultValue="devices" className="mb-8">
          <TabsList className="mb-4">
            <TabsTrigger value="devices">Devices</TabsTrigger>
            <TabsTrigger value="troubleshooting">Troubleshooting</TabsTrigger>
            <TabsTrigger value="commandHistory">Command History</TabsTrigger>
          </TabsList>
          
          <TabsContent value="troubleshooting">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center">
                  <Info className="mr-2 h-5 w-5" />
                  ESP32 Troubleshooting Guide
                </CardTitle>
                <CardDescription>
                  Common issues and their solutions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-yellow-50 rounded-md border border-yellow-200">
                  <h3 className="font-medium text-amber-800 mb-2">No Device Showing Up</h3>
                  <p className="text-amber-700 mb-2">If your ESP32 doesn't appear in the devices list:</p>
                  <ul className="list-disc pl-5 text-amber-700 text-sm space-y-1">
                    <li>Ensure the ESP32 is powered on and connected to the same network as this app</li>
                    <li>Check that the MAC address in your code matches the actual ESP32 MAC</li>
                    <li>Verify the ESP32 is successfully connecting to WiFi (check serial monitor)</li>
                    <li>Make sure the ESP32 is registering with Supabase (see serial output)</li>
                    <li>Try resetting the ESP32 by pressing the physical reset button</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-red-50 rounded-md border border-red-200">
                  <h3 className="font-medium text-red-800 mb-2">Commands Not Received by ESP32</h3>
                  <p className="text-red-700 mb-2">If your ESP32 isn't responding to commands:</p>
                  <ul className="list-disc pl-5 text-red-700 text-sm space-y-1">
                    <li>Check that the ESP32 is still connected to WiFi and hasn't crashed</li>
                    <li>Verify the ESP32 is checking for new commands in its main loop</li>
                    <li>Make sure the device_id in your ESP32 code matches what's in Supabase</li>
                    <li>Check for any error messages in the serial monitor</li>
                    <li>Try increasing the polling frequency in your ESP32 code</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
                  <h3 className="font-medium text-blue-800 mb-2">PIR Sensor Issues</h3>
                  <p className="text-blue-700 mb-2">If your PIR sensor is not working correctly:</p>
                  <ul className="list-disc pl-5 text-blue-700 text-sm space-y-1">
                    <li>Check all physical connections (VCC, GND, and Signal pins)</li>
                    <li>Verify the PIR sensor is receiving proper power (usually 3.3V or 5V)</li>
                    <li>Test the PIR sensor with a simple Arduino test sketch</li>
                    <li>Make sure the pin number in your code matches the physical connection</li>
                    <li>Try a different PIR sensor to rule out hardware failure</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-green-50 rounded-md border border-green-200">
                  <h3 className="font-medium text-green-800 mb-2">Debugging Steps</h3>
                  <p className="text-green-700 mb-2">Follow these steps to debug your ESP32:</p>
                  <ol className="list-decimal pl-5 text-green-700 text-sm space-y-1">
                    <li>Add more <code>Serial.println()</code> statements to your code to track execution</li>
                    <li>Check if your ESP32 is running out of memory or crashing (look for stack trace errors)</li>
                    <li>Register a test device (use the button above) to verify the Supabase connection</li>
                    <li>Use the "Command History" tab to check if commands are being sent correctly</li>
                    <li>Try simplifying your code to isolate the issue</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="devices">
            {!loading && devices.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertCircle className="mx-auto h-12 w-12 text-orange-500 mb-4" />
                  <h2 className="text-2xl font-semibold mb-2">No Devices Found</h2>
                  <p className="text-gray-500 mb-6">
                    No ESP32 devices have registered with the system yet.
                  </p>
                  <div className="space-y-4">
                    <Button onClick={refreshData}>
                      Refresh
                    </Button>
                    <div className="p-4 bg-yellow-50 rounded-md text-left">
                      <h3 className="font-medium text-amber-800 mb-2">Why is my device not showing up?</h3>
                      <ol className="list-decimal pl-5 text-amber-700 text-sm space-y-1">
                        <li>Check your ESP32 serial monitor output</li>
                        <li>Verify the ESP32 is connected to WiFi</li>
                        <li>Make sure the ESP32 is registering with Supabase</li>
                        <li>Check for any error messages in the serial monitor</li>
                        <li>Try resetting your ESP32</li>
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
                {devices.map(device => (
                  <Card key={device.id} className={`border-2 shadow-md ${isDeviceOnline(device.last_seen) ? 'border-green-200' : 'border-red-200'}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center">
                          <Cpu className="mr-2 h-5 w-5" />
                          {device.name || `Device ${device.device_id.substring(0, 8)}`}
                        </span>
                        <Badge 
                          variant={isDeviceOnline(device.last_seen) ? "default" : "destructive"}
                          className="ml-2"
                        >
                          {isDeviceOnline(device.last_seen) ? (
                            <><Wifi className="h-3 w-3 mr-1" /> Online</>
                          ) : (
                            <><WifiOff className="h-3 w-3 mr-1" /> Offline</>
                          )}
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        ID: {device.device_id}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-500">First Seen:</span>
                          <span>{device.created_at ? format(new Date(device.created_at), 'MMM d, h:mm:ss a') : 'Unknown'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Last Seen:</span>
                          <span>{device.last_seen ? format(new Date(device.last_seen), 'MMM d, h:mm:ss a') : 'Never'}</span>
                        </div>
                        
                        <div className="border-t pt-3 mt-3 grid grid-cols-2 gap-3">
                          <Button 
                            variant="outline"
                            disabled={sendingPing}
                            onClick={() => pingDevice(device.device_id)}
                            className="w-full"
                          >
                            {sendingPing ? 'Sending...' : 'Ping Device'}
                          </Button>
                          
                          <Button 
                            disabled={sendingDiagnostic}
                            onClick={() => sendDiagnosticCommand(device.device_id)}
                            className="w-full bg-amber-500 hover:bg-amber-600"
                          >
                            <ActivitySquare className="h-4 w-4 mr-2" />
                            {sendingDiagnostic ? 'Sending...' : 'Request Diagnostic'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                    
                    {!isDeviceOnline(device.last_seen) && (
                      <CardFooter className="pt-0">
                        <div className="flex items-center w-full p-2 bg-red-50 text-red-800 rounded-md">
                          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          <p className="text-xs">Device appears to be offline. Check your ESP32 connection.</p>
                        </div>
                      </CardFooter>
                    )}
                  </Card>
                ))}
              </div>
            )}
            
            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500">Loading device data...</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="commandHistory">
            <Card>
              <CardHeader>
                <CardTitle>Device Commands</CardTitle>
                <CardDescription>Recent commands sent to your ESP32</CardDescription>
              </CardHeader>
              <CardContent>
                <CommandHistory />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Command history component
const CommandHistory = () => {
  const [commands, setCommands] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchCommands();
    
    // Set up real-time subscription for new commands
    const channel = supabase
      .channel('command-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'device_commands'
        },
        (payload) => {
          console.log('Command update received:', payload);
          fetchCommands();
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);
  
  const fetchCommands = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('device_commands')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
          
      if (error) throw error;
      console.log('Fetched commands:', data);
      setCommands(data || []);
    } catch (error) {
      console.error('Error fetching commands:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <p className="text-center text-gray-500">Loading command history...</p>;
  }
  
  if (commands.length === 0) {
    return (
      <div className="text-center">
        <p className="text-gray-500 mb-4">No commands have been sent yet.</p>
        <div className="p-4 bg-blue-50 rounded-md text-left">
          <p className="text-blue-700">
            When you send commands like "Ping Device" or "Request Diagnostic", 
            they will appear here. This helps you track whether commands are 
            being properly sent to your ESP32.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {commands.map(cmd => (
        <div key={cmd.id} className="p-3 border rounded-md text-sm">
          <div className="flex justify-between">
            <span className="font-medium">{cmd.command_type}</span>
            <Badge variant={cmd.processed ? "success" : "outline"}>
              {cmd.processed ? "Processed" : "Pending"}
            </Badge>
          </div>
          <div className="text-gray-500 text-xs mt-1">
            Sent: {format(new Date(cmd.created_at), 'MMM d, h:mm:ss a')}
          </div>
          <div className="text-gray-600 mt-1 font-mono text-xs bg-gray-50 p-1 rounded">
            Params: {JSON.stringify(cmd.params)}
          </div>
          <div className="text-gray-500 text-xs mt-1">
            Device ID: {cmd.device_id}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DeviceDiagnostic;
